import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, Wallet, TrendingDown, MessageSquareWarning, BedDouble, UserX, ArrowRight, Clock, AlertTriangle, Banknote, Landmark, RefreshCw, Receipt } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { useAuth } from "@/context/AuthContext";

const KPI = ({ label, value, sub, Icon, tid, tone = "primary", to }) => {
  const inner = (
    <Card
      className="kpi-card p-6 border-border bg-white flex flex-col justify-between h-full hover:shadow-md transition-shadow"
      data-testid={tid}
    >
      <div className="flex items-start justify-between">
        <div className="eyebrow text-muted-foreground">{label}</div>
        <div
          className={`w-9 h-9 rounded-md flex items-center justify-center ${
            tone === "accent"
              ? "bg-accent/10 text-accent"
              : tone === "warn"
              ? "bg-destructive/10 text-destructive"
              : tone === "muted"
              ? "bg-muted-foreground/10 text-muted-foreground"
              : "bg-primary/10 text-primary"
          }`}
        >
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="mt-6">
        <div className="font-display text-4xl font-bold leading-none">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-2">{sub}</div>}
      </div>
    </Card>
  );
  return to ? <Link to={to} className="block h-full">{inner}</Link> : inner;
};

// Clickable KPI card used for the daily-collection tiles (Total / Cash / Bank)
const ClickCard = ({ label, value, Icon, tone = "primary", onClick, tid }) => {
  const toneClass =
    tone === "green"
      ? "bg-emerald-500/10 text-emerald-600"
      : tone === "accent"
      ? "bg-accent/10 text-accent"
      : tone === "warn"
      ? "bg-destructive/10 text-destructive"
      : "bg-primary/10 text-primary";
  const fmt = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN")}`;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={tid}
      className="text-left w-full"
    >
      <Card className="kpi-card p-5 border-border bg-white hover:shadow-md transition-shadow h-full">
        <div className="flex items-start justify-between">
          <div className="eyebrow text-muted-foreground">{label}</div>
          <div className={`w-9 h-9 rounded-md flex items-center justify-center ${toneClass}`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
        <div className="mt-4 font-display text-3xl font-bold leading-none">{fmt(value)}</div>
        <div className="text-xs text-muted-foreground mt-2">Click to view breakdown</div>
      </Card>
    </button>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  // v14 — Daily collection
  const [collection, setCollection] = useState(null);
  const [collectionRange, setCollectionRange] = useState("today"); // today | yesterday | week | month
  const [drillMode, setDrillMode] = useState(null);                // cash | bank | all | null

  // v20.1 — Memoise the drill-down filter so it doesn't recompute on every render
  const filteredTransactions = useMemo(() => {
    if (!collection?.transactions) return [];
    if (drillMode === "all" || !drillMode) return collection.transactions;
    // v26 — new guest-only filters
    if (drillMode === "guest") {
      return collection.transactions.filter((t) => t.entity_type === "stay_guest" || t.entity_type === "mess_guest");
    }
    if (drillMode === "guest_cash") {
      return collection.transactions.filter((t) => (t.entity_type === "stay_guest" || t.entity_type === "mess_guest") && t.book === "cash_book");
    }
    if (drillMode === "guest_bank") {
      return collection.transactions.filter((t) => (t.entity_type === "stay_guest" || t.entity_type === "mess_guest") && t.book === "bank_book");
    }
    const book = drillMode === "cash" ? "cash_book" : "bank_book";
    return collection.transactions.filter((t) => t.book === book);
  }, [collection, drillMode]);

  const rangeDates = (r) => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (r === "today") return { from_date: iso(today), to_date: iso(today) };
    if (r === "yesterday") { const y = new Date(today); y.setDate(y.getDate()-1); return { from_date: iso(y), to_date: iso(y) }; }
    if (r === "week") { const s = new Date(today); s.setDate(s.getDate()-6); return { from_date: iso(s), to_date: iso(today) }; }
    if (r === "month") { const s = new Date(today.getFullYear(), today.getMonth(), 1); return { from_date: iso(s), to_date: iso(today) }; }
    return { from_date: iso(today), to_date: iso(today) };
  };

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const { from_date, to_date } = rangeDates(collectionRange);
    api.get(`/dashboard/collection?from_date=${from_date}&to_date=${to_date}`)
      .then((r) => setCollection(r.data)).catch(() => setCollection(null));
  }, [collectionRange]);

  const canSeeMoney = ["admin", "director", "accountant", "warden", "hostel_manager"].includes(user?.role);

  const fmt = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-muted-foreground">Overview</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold mt-1">
            Good day, {user?.name?.split(" ")[0] || "there"}.
          </h1>
          <p className="text-muted-foreground mt-2 max-w-xl">
            Live snapshot of occupancy, revenue and open cases across the hostel.
          </p>
        </div>
        <div className="text-right">
          <div className="eyebrow text-muted-foreground">Total Capacity</div>
          <div className="font-display text-2xl font-bold">{stats?.total_beds ?? 480} beds</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <KPI
          label="Active Residents"
          value={stats?.total_residents ?? "—"}
          sub={
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="kpi-residents-breakdown">
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary" />
                <span data-testid="kpi-residents-male">M {stats?.male_residents ?? 0}</span>
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent" />
                <span data-testid="kpi-residents-female">F {stats?.female_residents ?? 0}</span>
              </span>
              <span className="text-muted-foreground/70">·</span>
              <span data-testid="kpi-residents-occupancy">{stats?.occupancy_pct ?? 0}% occupancy</span>
            </span>
          }
          Icon={Users}
          tid="kpi-residents"
        />
        <KPI
          label="Dropouts"
          value={stats?.dropout_count ?? "—"}
          sub={stats?.dropout_count ? "Excluded from active" : "None recorded"}
          Icon={UserX}
          tid="kpi-dropouts"
          tone="muted"
          to="/residents?status=dropout"
        />
        <KPI
          label="Vacant Beds"
          value={stats?.vacancy ?? "—"}
          sub="Available for allocation"
          Icon={BedDouble}
          tid="kpi-vacancy"
        />
        <KPI
          label="Revenue Collected"
          value={stats ? fmt(stats.revenue_total) : "—"}
          sub="All time"
          Icon={Wallet}
          tid="kpi-revenue"
          tone="accent"
        />
        <KPI
          label="Outstanding Dues"
          value={stats ? fmt(stats.outstanding_total) : "—"}
          sub={`${stats?.open_complaints ?? 0} complaints open`}
          Icon={TrendingDown}
          tid="kpi-outstanding"
          tone="warn"
        />
      </div>

      {/* v31 — Petty Cash card (this month). Click → open Reports & P&L → Petty Cash tab */}
      {canSeeMoney && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="petty-cash-strip">
          <KPI
            label="Petty Cash · This Month"
            value={stats ? fmt(stats.petty_cash_month_total) : "—"}
            sub={`${stats?.petty_cash_month_count ?? 0} entries · click to view details`}
            Icon={Receipt}
            tid="kpi-petty-cash"
            tone="accent"
            to="/petty-cash"
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-6 border-border bg-white" data-testid="chart-revenue">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="eyebrow text-muted-foreground">Trend</div>
              <div className="font-display text-2xl font-bold mt-1">Monthly Revenue</div>
            </div>
            <div className="text-xs text-muted-foreground">Last 6 months</div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats?.monthly_revenue || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E1E7DD" />
                <XAxis dataKey="month" stroke="#5B6B62" fontSize={12} />
                <YAxis stroke="#5B6B62" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    background: "#0B4A3F", border: "none", color: "#F7F9F6",
                    borderRadius: 6, fontSize: 12,
                  }}
                />
                <Bar dataKey="revenue" fill="#0B4A3F" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-border bg-white flex flex-col" data-testid="card-status">
          <div className="eyebrow text-muted-foreground">Operations</div>
          <div className="font-display text-2xl font-bold mt-1 mb-6">System Health</div>
          <div className="space-y-4">
            <Row label="Open Complaints" value={stats?.open_complaints ?? 0} />
            <Row label="Occupancy" value={`${stats?.occupancy_pct ?? 0}%`} />
            <Row label="Beds Available" value={stats?.vacancy ?? 0} />
            <Row label="Dropouts (total)" value={stats?.dropout_count ?? 0} />
          </div>
          <div className="mt-auto pt-6 border-t border-border">
            <div className="flex items-center gap-2 text-sm">
              <MessageSquareWarning className="w-4 h-4 text-accent" />
              <span className="text-muted-foreground">
                Ask the AI copilot for a full ops brief.
              </span>
            </div>
          </div>
        </Card>
      </div>

      {/* v14 — DAILY COLLECTION CARDS */}
      {canSeeMoney && collection && (
        <Card className="p-6 border-border bg-white" data-testid="card-collection">
          <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="eyebrow text-muted-foreground">Collections</div>
              <div className="font-display text-2xl font-bold mt-1">
                {collectionRange === "today" ? "Today's" : collectionRange === "yesterday" ? "Yesterday's" : collectionRange === "week" ? "This Week's" : "This Month's"} Collection
              </div>
              <p className="text-sm text-muted-foreground mt-1 font-mono">
                {collection.from_date} → {collection.to_date} • {collection.transaction_count} txn(s)
              </p>
            </div>
            <div className="flex gap-1 flex-wrap">
              {["today","yesterday","week","month"].map((r) => (
                <Button key={r} size="sm"
                  variant={collectionRange === r ? "default" : "outline"}
                  onClick={() => setCollectionRange(r)}
                  data-testid={`collection-range-${r}`}
                  className="capitalize">{r}</Button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ClickCard label="Total Collected" value={collection.total} Icon={Wallet}
              tone="primary" onClick={() => setDrillMode("all")} tid="col-total" />
            <ClickCard label="Cash Collection" value={collection.total_cash} Icon={Banknote}
              tone="green" onClick={() => setDrillMode("cash")} tid="col-cash" />
            <ClickCard label="Bank / Online" value={collection.total_bank} Icon={Landmark}
              tone="accent" onClick={() => setDrillMode("bank")} tid="col-bank" />
          </div>
          {/* v26 — Guest-only breakdown of the same collection window */}
          {(collection.guest_total || 0) > 0 && (
            <div className="mt-4 pt-4 border-t border-border" data-testid="dash-guest-breakdown">
              <div className="flex items-center justify-between mb-3">
                <div className="eyebrow text-muted-foreground">Guest Collections</div>
                <div className="text-xs text-muted-foreground font-mono">
                  {collection.guest_transaction_count || 0} guest txn(s) · included in totals above
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <ClickCard label="Guest Total" value={collection.guest_total} Icon={Wallet}
                  tone="primary" onClick={() => setDrillMode("guest")} tid="col-guest-total" />
                <ClickCard label="Guest · Cash" value={collection.guest_cash} Icon={Banknote}
                  tone="green" onClick={() => setDrillMode("guest_cash")} tid="col-guest-cash" />
                <ClickCard label="Guest · Online" value={collection.guest_bank} Icon={Landmark}
                  tone="accent" onClick={() => setDrillMode("guest_bank")} tid="col-guest-bank" />
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Drill-down dialog */}
      <Dialog open={!!drillMode} onOpenChange={(v) => !v && setDrillMode(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="collection-drill-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {drillMode === "cash" ? "Cash Collection"
                : drillMode === "bank" ? "Bank / Online Collection"
                : drillMode === "guest" ? "Guest Collection"
                : drillMode === "guest_cash" ? "Guest · Cash Collection"
                : drillMode === "guest_bank" ? "Guest · Online Collection"
                : "Total Collection"} — {collection?.from_date}
              {collection?.to_date !== collection?.from_date && ` → ${collection?.to_date}`}
            </DialogTitle>
          </DialogHeader>
          {collection && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="drill-table">
                <thead>
                  <tr className="text-left text-muted-foreground eyebrow border-b border-border">
                    <th className="py-2 pr-3">Time</th>
                    <th className="py-2 pr-3">Resident</th>
                    <th className="py-2 pr-3">Receipt</th>
                    <th className="py-2 pr-3">Mode</th>
                    <th className="py-2 pr-3">Reference</th>
                    <th className="py-2 pr-3 text-right">Amount</th>
                    <th className="py-2 pr-3">Collected By</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((t) => (
                      <tr key={t.id} className="border-b border-border/40 hover:bg-secondary/40"
                          data-testid={`drill-row-${t.id}`}>
                        <td className="py-2 pr-3 font-mono text-xs">{new Date(t.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</td>
                        <td className="py-2 pr-3 font-medium">
                          {t.resident_name}

                        </td>
                        <td className="py-2 pr-3 font-mono text-xs">{t.receipt_no}</td>
                        <td className="py-2 pr-3 uppercase text-xs">{t.mode?.replace("_", " ")}</td>
                        <td className="py-2 pr-3 font-mono text-xs text-muted-foreground truncate max-w-[140px]">{t.reference || "—"}</td>
                        <td className="py-2 pr-3 text-right font-mono font-semibold">₹{Number(t.amount).toLocaleString("en-IN")}</td>
                        <td className="py-2 pr-3 text-xs">{t.collected_by || "—"} {t.collected_by_role && <span className="text-muted-foreground">({t.collected_by_role})</span>}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
              <div className="pt-3 mt-3 border-t border-border flex justify-between text-sm">
                <span className="text-muted-foreground">Total for view:</span>
                <span className="font-mono font-bold" data-testid="drill-total">
                  ₹{Number(
                    drillMode === "cash" ? collection.total_cash
                    : drillMode === "bank" ? collection.total_bank
                    : drillMode === "guest" ? (collection.guest_total || 0)
                    : drillMode === "guest_cash" ? (collection.guest_cash || 0)
                    : drillMode === "guest_bank" ? (collection.guest_bank || 0)
                    : collection.total
                  ).toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* SUBSCRIPTIONS EXPIRING SOON */}
      {stats?.expiring && (
        <Card className="p-6 border-border bg-white" data-testid="card-expiring">
          <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="eyebrow text-muted-foreground">Renewals</div>
              <div className="font-display text-2xl font-bold mt-1">Subscriptions Expiring Soon</div>
              <p className="text-sm text-muted-foreground mt-1">
                Click any bucket to open the full list. Hostel and Mess subscriptions are tracked separately.
              </p>
            </div>
            <div className="flex gap-2">
              <Link to="/residents/expiring?bucket=expired">
                <Button size="sm" data-testid="quick-renew-btn">
                  <RefreshCw className="w-3 h-3 mr-1" /> Renew Resident
                </Button>
              </Link>
              <Link to="/residents/expiring?bucket=30">
                <Button variant="outline" size="sm" data-testid="view-all-expiring">
                  Open full view <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <ExpBucket to="/residents/expiring?bucket=today"   label="Today"        combined={stats.expiring.today}    hostel={stats.expiring.hostel?.today}    mess={stats.expiring.mess?.today}    tone="red"    tid="exp-today" />
            <ExpBucket to="/residents/expiring?bucket=3"       label="Next 3 Days"  combined={stats.expiring.next_3}   hostel={stats.expiring.hostel?.next_3}   mess={stats.expiring.mess?.next_3}   tone="orange" tid="exp-3" />
            <ExpBucket to="/residents/expiring?bucket=7"       label="Next 7 Days"  combined={stats.expiring.next_7}   hostel={stats.expiring.hostel?.next_7}   mess={stats.expiring.mess?.next_7}   tone="yellow" tid="exp-7" />
            <ExpBucket to="/residents/expiring?bucket=15"      label="Next 15 Days" combined={stats.expiring.next_15}  hostel={stats.expiring.hostel?.next_15}  mess={stats.expiring.mess?.next_15}  tone="yellow" tid="exp-15" />
            <ExpBucket to="/residents/expiring?bucket=30"      label="Next 30 Days" combined={stats.expiring.next_30}  hostel={stats.expiring.hostel?.next_30}  mess={stats.expiring.mess?.next_30}  tone="green"  tid="exp-30" />
            <ExpBucket to="/residents/expiring?bucket=expired" label="Expired"      combined={stats.expiring.expired}  hostel={stats.expiring.hostel?.expired}  mess={stats.expiring.mess?.expired}  tone="red"    tid="exp-expired" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 pt-4 border-t border-border">
            <MiniRow label="Renewed this month" value={stats.expiring.renewed_this_month} Icon={Clock} tone="green" />
            <MiniRow label="Pending renewals" value={stats.expiring.expired} Icon={AlertTriangle} tone="red" />
            <MiniRow label="Expiring next 7d" value={stats.expiring.next_7} Icon={Clock} tone="yellow" />
            <MiniRow label="Total outstanding" value={`₹${Number(stats.expiring.outstanding_total || 0).toLocaleString("en-IN")}`} Icon={Wallet} tone="orange" />
          </div>
        </Card>
      )}

      {/* v23.1 — Expired residents mini-table (Name / Monthly / Pending / Gross / Paid / Remaining) */}
      {stats?.expiring?.expired > 0 && <ExpiredMiniTable />}

      {/* RECENT DROPOUTS */}
      {stats?.recent_dropouts?.length > 0 && (
        <Card className="p-6 border-border bg-white" data-testid="card-recent-dropouts">
          <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="eyebrow text-muted-foreground">Attention</div>
              <div className="font-display text-2xl font-bold mt-1">
                Recent Dropouts
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                These residents have been moved out of active occupancy and their beds released.
              </p>
            </div>
            <Link to="/residents?status=dropout">
              <Button variant="outline" size="sm" data-testid="view-all-dropouts">
                View all <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground eyebrow border-b border-border">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Room</th>
                  <th className="py-2 pr-4">Reason</th>
                  <th className="py-2 pr-4">Date</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_dropouts.map((d) => (
                  <tr key={d.id} className="border-b border-border/40 hover:bg-secondary/40"
                      data-testid={`dropout-row-${d.id}`}>
                    <td className="py-2 pr-4 font-medium">{d.name}</td>
                    <td className="py-2 pr-4 font-mono">
                      {d.room_no || "—"}{d.bed_no ? `-${d.bed_no}` : ""}
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground max-w-xs truncate">
                      {d.dropout_reason || "—"}
                    </td>
                    <td className="py-2 pr-4 font-mono text-xs">
                      {(d.dropout_date || "").slice(0, 10) || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-dashed border-border last:border-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="font-display font-bold">{value}</span>
  </div>
);

const TONE_DOT = { red: "bg-destructive", orange: "bg-orange-500", yellow: "bg-yellow-500", green: "bg-emerald-500" };
const TONE_BORDER = { red: "border-destructive/30", orange: "border-orange-300", yellow: "border-yellow-300", green: "border-emerald-300" };

function ExpiredMiniTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/reports/outstanding")
      .then((r) => {
        const today = new Date().toISOString().slice(0, 10);
        const expired = (r.data || []).filter(
          (x) => x.expired_balance > 0 || (x.expiry_date && x.expiry_date < today)
        );
        setRows(expired);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);
  const grossTotal = rows.reduce((s, r) => s + Number(r.balance || 0), 0);
  const paidTotal = rows.reduce((s, r) => s + Number(r.total_paid || 0), 0);
  const remainingTotal = rows.reduce((s, r) => s + Number(r.expired_balance || r.balance || 0), 0);
  return (
    <Card className="p-6 border-border bg-white" data-testid="card-expired-detail">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="eyebrow text-destructive">Already Expired</div>
          <div className="font-display text-2xl font-bold mt-1">Expired Residents · Full Detail</div>
          <p className="text-sm text-muted-foreground mt-1">
            Monthly rate × pending months from actual billing periods (not joining date).
          </p>
        </div>
        <div className="flex gap-4 text-right">
          <div><div className="eyebrow text-muted-foreground">Gross</div>
               <div className="font-mono font-bold text-lg" data-testid="expired-gross">₹{grossTotal.toLocaleString("en-IN")}</div></div>
          <div><div className="eyebrow text-muted-foreground">Paid</div>
               <div className="font-mono font-bold text-lg text-primary">₹{paidTotal.toLocaleString("en-IN")}</div></div>
          <div><div className="eyebrow text-muted-foreground">Remaining</div>
               <div className="font-mono font-bold text-lg text-destructive" data-testid="expired-remaining">₹{remainingTotal.toLocaleString("en-IN")}</div></div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="expired-table">
          <thead><tr className="text-left text-muted-foreground eyebrow border-b border-border">
            <th className="py-2 pr-3">Name</th>
            <th className="py-2 pr-3 text-right">Monthly Hostel</th>
            <th className="py-2 pr-3 text-right">Monthly Mess</th>
            <th className="py-2 pr-3">Pending Mo.</th>
            <th className="py-2 pr-3 text-right">Gross</th>
            <th className="py-2 pr-3 text-right">Paid</th>
            <th className="py-2 pr-3 text-right">Remaining</th>
            <th className="py-2 pr-3">Expiry</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && rows.slice(0, 10).map((r) => (
              <tr key={r.resident_id} className="border-b border-border/50 hover:bg-secondary/30" data-testid={`expired-row-${r.resident_id}`}>
                <td className="py-2 pr-3 font-medium">{r.name}</td>
                <td className="py-2 pr-3 text-right font-mono">{r.monthly_hostel > 0 ? `₹${Number(r.monthly_hostel).toLocaleString("en-IN")}` : "—"}</td>
                <td className="py-2 pr-3 text-right font-mono">{r.monthly_mess > 0 ? `₹${Number(r.monthly_mess).toLocaleString("en-IN")}` : "—"}</td>
                <td className="py-2 pr-3">{r.pending_months > 0 ? <Badge variant="destructive" className="font-mono text-[10px]">{r.pending_months} mo.</Badge> : "—"}</td>
                <td className="py-2 pr-3 text-right font-mono">₹{Number(r.total_billed || 0).toLocaleString("en-IN")}</td>
                <td className="py-2 pr-3 text-right font-mono text-primary">₹{Number(r.total_paid || 0).toLocaleString("en-IN")}</td>
                <td className="py-2 pr-3 text-right font-mono font-bold text-destructive">₹{Number(r.expired_balance || r.balance || 0).toLocaleString("en-IN")}</td>
                <td className="py-2 pr-3 font-mono text-xs">{r.expiry_date || "—"}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">No expired residents. All active ✨</td></tr>}
          </tbody>
        </table>
        {rows.length > 10 && (
          <div className="mt-3 text-center">
            <Link to="/outstanding" className="text-primary text-sm hover:underline">View all {rows.length} expired →</Link>
          </div>
        )}
      </div>
    </Card>
  );
}


const ExpBucket = ({ to, label, combined, hostel, mess, tone, tid }) => (
  <Link to={to} className="block" data-testid={tid}>
    <div className={`p-4 rounded-md border ${TONE_BORDER[tone]} bg-white hover:shadow-md transition-shadow`}>
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${TONE_DOT[tone]}`} />
        <span className="eyebrow text-muted-foreground">{label}</span>
      </div>
      <div className="font-display text-3xl font-bold mt-2">{combined ?? 0}</div>
      <div className="flex items-center gap-3 mt-1.5 text-[11px]">
        <span className="inline-flex items-center gap-1" data-testid={`${tid}-hostel`}>
          <span className="w-1.5 h-1.5 rounded-full bg-primary" />
          <span className="text-muted-foreground">Hostel</span>
          <span className="font-semibold text-foreground">{hostel ?? 0}</span>
        </span>
        <span className="inline-flex items-center gap-1" data-testid={`${tid}-mess`}>
          <span className="w-1.5 h-1.5 rounded-full bg-accent" />
          <span className="text-muted-foreground">Mess</span>
          <span className="font-semibold text-foreground">{mess ?? 0}</span>
        </span>
      </div>
    </div>
  </Link>
);

const MiniRow = ({ label, value, Icon, tone }) => (
  <div className="flex items-center gap-3 p-3 rounded-md border border-border bg-secondary/30">
    <span className={`w-8 h-8 rounded-md flex items-center justify-center ${
      tone === "red" ? "bg-destructive/10 text-destructive" :
      tone === "orange" ? "bg-orange-100 text-orange-700" :
      tone === "yellow" ? "bg-yellow-100 text-yellow-700" :
      "bg-emerald-100 text-emerald-700"
    }`}>
      <Icon className="w-4 h-4" />
    </span>
    <div>
      <div className="eyebrow text-muted-foreground">{label}</div>
      <div className="font-display font-bold text-lg leading-tight mt-0.5">{value ?? "—"}</div>
    </div>
  </div>
);
